/**
 * M3 — universal cart-line renderer for sandbox-loaded modules.
 *
 * Built-in modules ship their own `CartLineSummary` under
 * `processes/<name>/`. Sandbox modules drive through this generic
 * renderer instead. The component:
 *
 * 1. Renders `material_code` + `quantity` as the two universal
 *    editable axes (every process declares them).
 * 2. Renders the line's parameters (`dimensions`) and any other
 *    `config_snapshot` value read-only, named by the operation's own
 *    declaration (`dimension_labels`, KAN-1432) — never by the wire key.
 * 3. Surfaces `cost_breakdown` semantic axes when the BE snapshot
 *    carries them (laser → area/cut/pierce, 3D → material/machine,
 *    CNC → time/tool_changes, …).
 *
 * Inline edit is intentionally minimal in V1: material + quantity
 * only. Process-specific configurator widgets stay in the
 * configurator page (`DynamicConfigurator`); the cart row's job is
 * to summarise + let the user bump quantity without leaving the cart.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useUpdateCartLine } from "@/api/queries";
import { useMaxQtyPerLine } from "@/api/queries/branding";
import { formatDimensions } from "@/lib/dimensionDisplay";
import { moneyMapFromPair } from "@/lib/money";
import { formatOrderLineDimensions, orderLineLabelMap } from "@/lib/orderDisplay";
import { Stepper } from "@/components/ui/Stepper";
import type {
  CartLineSummaryProps,
  CartLineUpdatePayload,
} from "@/processes/types";

interface PriceSnapshot {
  cost_breakdown?: Record<string, number | string>;
  effective_breakdown_czk?: Record<string, number | string>;
  /** KAN-284 b2 — same map in minor units; wins over the `_czk` twin. */
  effective_breakdown_minor?: Record<string, number>;
  cost_labels?: Record<string, string>;
  unit_price_czk?: number;
  currency_code?: string | null;
}

function readPriceSnapshot(value: unknown): PriceSnapshot | null {
  if (!value || typeof value !== "object") return null;
  return value as PriceSnapshot;
}

export function DynamicCartLineSummary({
  line,
  configSnapshot,
  onUpdateLine,
}: CartLineSummaryProps): JSX.Element {
  const { t, i18n } = useTranslation("cart");
  const update = useUpdateCartLine();

  const [draftQty, setDraftQty] = useState<number>(line.quantity);
  useEffect(() => {
    setDraftQty(line.quantity);
  }, [line.quantity]);
  // KAN-1903 — the shop's per-line cap (the number the cart enforces), not
  // a constant of this field. ``overCap`` keeps what the customer tried to
  // type so the clamp is explained instead of silent.
  const maxQty = useMaxQtyPerLine();
  const [overCap, setOverCap] = useState<number | null>(null);

  const dispatchUpdate = (lineId: number, payload: CartLineUpdatePayload) => {
    if (onUpdateLine) onUpdateLine(lineId, payload);
    else update.mutate({ lineId, payload });
  };

  const onQtyChange = (qty: number) => {
    if (maxQty === null || qty < maxQty) setOverCap(null);
    setDraftQty(qty);
    if (qty !== line.quantity) {
      // Quantity-only edit — the BE PATCH is a TRUE PARTIAL MERGE, so
      // absent dimensions keep the stored values.
      dispatchUpdate(line.id, {
        material_code: line.material_code,
        quantity: qty,
      });
    }
  };

  // Read-only extras: everything in config_snapshot except the universal
  // identity keys, the nested dimensions dict (rendered via the line's own
  // variant display) and any flat mirror of a dimension value.
  const snapshotDimensions =
    configSnapshot && typeof configSnapshot.dimensions === "object"
      ? (configSnapshot.dimensions as Record<string, unknown>)
      : {};
  const extraEntries: Array<[string, unknown]> = configSnapshot
    ? Object.entries(configSnapshot).filter(
        ([key, value]) =>
          key !== "material_code" &&
          key !== "quantity" &&
          key !== "dimensions" &&
          !(key in snapshotDimensions) &&
          // Nested bags (material facts, …) are engine input, not a choice.
          (value === null || typeof value !== "object"),
      )
    : [];
  const modifierLabel = t("col.finish");
  const parameters =
    line.dimensions && Object.keys(line.dimensions).length > 0
      ? formatOrderLineDimensions(
          { dimensions: line.dimensions, dimension_labels: line.dimension_labels },
          modifierLabel,
          { locale: i18n.language },
        )
      : "";
  const extras =
    extraEntries.length > 0
      ? formatDimensions(
          Object.fromEntries(extraEntries),
          orderLineLabelMap(
            { dimensions: line.dimensions ?? {}, dimension_labels: line.dimension_labels },
            modifierLabel,
          ),
          i18n.language,
        )
      : "";

  // Cost breakdown surfacing — prefer effective (post-markup) over raw.
  const priceSnapshot = readPriceSnapshot(
    (line as unknown as { price_snapshot?: unknown }).price_snapshot,
  );
  // The minor spelling wins where present — a sub-unit component would
  // otherwise render rounded here while ``PriceBreakdown`` shows it exact.
  const effective = priceSnapshot
    ? moneyMapFromPair(
        priceSnapshot.effective_breakdown_minor,
        priceSnapshot.effective_breakdown_czk,
        priceSnapshot.currency_code,
      )
    : {};
  const breakdown =
    Object.keys(effective).length > 0
      ? effective
      : (priceSnapshot?.cost_breakdown ?? null);
  // Admin-authored component labels (single source of truth); fall back to the
  // raw axis key when a component declares none.
  const breakdownLabels = priceSnapshot?.cost_labels ?? {};

  return (
    <div className="flex flex-col gap-2 text-sm">
      <div className="flex items-center gap-3">
        <span className="font-mono text-xs text-ink-3">
          {t("col.material", { defaultValue: "Materiál" })}:
        </span>
        <span className="text-ink">
          {line.material_display_name || line.material_code}
        </span>
      </div>

      <div className="flex items-center gap-3">
        <span className="font-mono text-xs text-ink-3">
          {t("col.quantity", { defaultValue: "Množství" })}:
        </span>
        <Stepper
          value={draftQty}
          onChange={onQtyChange}
          min={1}
          max={maxQty ?? undefined}
          onExceedMax={setOverCap}
          ariaLabel={t("col.quantity")}
        />
      </div>
      {overCap !== null && maxQty !== null && (
        <p
          className="text-xs text-warn"
          data-testid={`cart-line-${line.id}-qty-over-cap`}
        >
          {t("line.qtyOverCap", {
            quantity: overCap.toLocaleString(i18n.language),
            max_quantity: maxQty.toLocaleString(i18n.language),
          })}
        </p>
      )}

      {[parameters, extras].filter((text) => text && text !== "—").map((text) => (
        <p
          key={text}
          className="text-xs text-ink-2"
          data-testid={`cart-line-${line.id}-parameters`}
        >
          {text}
        </p>
      ))}

      {breakdown && Object.keys(breakdown).length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-3">
          {Object.entries(breakdown).map(([axis, value]) => (
            <span key={axis}>
              <span className="font-mono">{breakdownLabels[axis] ?? axis}:</span>{" "}
              <span className="text-ink">{String(value)} Kč</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default DynamicCartLineSummary;
